import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  ViewChild,
  ElementRef,
  AfterViewInit,
  computed,
  input,
  OnInit,
  signal,
  effect,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { getAuthUser } from 'libs/shared/data/stores/auth/auth.selectors';
import { ChatActions } from 'libs/shared/data/stores/chat/chat.actions';
import {
  getChatMessagesByRoomId,
  getMessageLoading,
} from 'libs/shared/data/stores/chat/chat.selectors';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { filter, switchMap, tap, map } from 'rxjs';

import { fetchUserById, selectUserList } from '@offconon/shared/data/stores/user';
import { MonogramComponent } from '@offconon/shared/ui/monogram';
import { WebSocketService } from '@offconon/shared/utils/services/websocket';

@Component({
  selector: 'offconon-chat-message-content',
  imports: [CommonModule, MonogramComponent, SkeletonModule, TranslateModule, ButtonModule],
  templateUrl: './chat-message-content.html',
  styleUrls: ['./chat-message-content.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageContentComponent implements AfterViewInit, OnInit {
  @ViewChild('scrollChat') private scrollChat!: ElementRef;
  roomId = input.required<number>();

  webSocketService = inject(WebSocketService);
  private store = inject(Store);

  isScrolledToBottom = signal(true);

  messageLoading = toSignal(
    toObservable(this.roomId).pipe(
      filter((roomId): roomId is number => roomId !== undefined),
      switchMap((roomId) => this.store.select(getMessageLoading(roomId))),
    ),
  );

  messages = toSignal(
    toObservable(this.roomId).pipe(
      filter((roomId): roomId is number => roomId !== undefined),
      tap((roomId) => {
        // Fetch messages whenever roomId changes
        this.store.dispatch(ChatActions.fetchMessages({ roomId }));
        // Reset scroll after a short delay
        setTimeout(() => this.scrollToBottom(), 100);
      }),
      switchMap((roomId) => this.store.select(getChatMessagesByRoomId(roomId))),
      map((messages) => {
        return messages || [];
      }),
      tap((messages) => {
        const userIds = [...new Set(messages.map((m) => m?.user_id))];
        if (userIds.every((id) => typeof id === 'number')) {
          this.loadUserData(userIds);
        }
        setTimeout(() => this.scrollToBottom(), 100);
      }),
    ),
    { initialValue: [] },
  );

  users = toSignal(this.store.select(selectUserList), { initialValue: [] });
  currentUser = toSignal(this.store.select(getAuthUser));

  messagesWithUsers = computed(() => {
    return this.messages().map((message) => {
      const user = this.users()?.find((user) => user?.user_id === message?.user_id);
      const isOutgoing = this.currentUser()?.id === message?.user_id;

      //log('isOutgoing', { message, user, isOutgoing });
      return { message, user, isOutgoing };
    });
  });

  constructor() {
    this.webSocketService.onMessage().subscribe((message) => {
      this.store.dispatch(ChatActions.addMessage({ message }));
      this.scrollToBottom();
    });
    // Auto-scroll to bottom when new messages arrive
    effect(() => {
      const messages = this.messagesWithUsers();
      if (messages && this.isScrolledToBottom()) {
        setTimeout(() => this.scrollToBottom(), 0);
      }
    });
  }

  ngOnInit() {
    this.store.dispatch(
      ChatActions.fetchMessages({
        roomId: this.roomId(),
      }),
    );
  }

  loadUserData(userIds: number[]) {
    userIds.forEach((id) => {
      this.store.dispatch(fetchUserById({ id }));
    });
  }

  ngAfterViewInit() {
    this.scrollToBottom();
    // Add scroll event listener
    this.scrollChat.nativeElement.addEventListener('scroll', () => this.checkScrollPosition());
  }

  private checkScrollPosition() {
    const element = this.scrollChat.nativeElement;
    const atBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 1;
    this.isScrolledToBottom.set(atBottom);
  }

  scrollToBottom() {
    if (this.scrollChat) {
      const element = this.scrollChat.nativeElement;
      element.scrollTop = element.scrollHeight;
    }
  }

  isImageUrl(url: string | undefined): boolean {
    if (!url) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    const baseUrl = url.split('?')[0].toLowerCase();
    return imageExtensions.some((extension) => baseUrl.endsWith(extension));
  }

  isVideoUrl(url: string | undefined): boolean {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.avi', '.webm', '.ogg'];
    const baseUrl = url.split('?')[0].toLowerCase();
    return videoExtensions.some((extension) => baseUrl.endsWith(extension));
  }

  isAudioUrl(url: string | undefined): boolean {
    if (!url) return false;
    const audioExtensions = ['.mp3', '.wav', '.ogg', '.flac', '.aac'];
    const baseUrl = url.split('?')[0].toLowerCase();
    return audioExtensions.some((extension) => baseUrl.endsWith(extension));
  }

  isYouTubeUrl(url: string | undefined): string {
    if (!url) return '';

    const youtubePattern =
      /(?:youtube\.com\/(?:[^/]+\/.*\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(youtubePattern);

    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
    return '';
  }
}
